import moment from "moment";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { deleteAlert } from "../../../components/alerts";
import GlobalButton from "../../../components/button";
import DynamicTable from "../../../components/dynamicTable";
import AppHeader from "../../../components/header";
import useDebounce from "../../../components/hooks/useDebounce";
import AppLoader from "../../../components/loader";
import { AppContext } from "../../../lib/Context";
import {
  deleteContract,
  exportContract,
  getAllContract,
  updateContract,
  updateContractStatuses,
} from "../../../services/ContractApi";
import { handleOpenSnackBar } from "../../../utils/helper";
import SearchTab from "./SearchTab";
import { useDispatch, useSelector } from "react-redux";
import { resetLeadSearchFilter } from "../../../lib/redux/slices/searchKeysSlice";
import {
  resetPagination,
  setCurrentPage,
  setPageLimit,
  setTotalCount,
  setTotalPages,
} from "../../../lib/redux/slices/contractPaginateSlice";
import { Stack } from "@mui/material";
import { setPhoneValidation } from "../../../lib/redux/slices/phoneValidationSlice";
import { getAdminAgentDetails } from "../../../services/AgentApi";
import { setSignedDate } from "../../../lib/redux/slices/signedDateSlice";

const CANCEL_SALE_STATUS = [
  "Cancel Pre-Live - In Contract",
  "Cancel Pre-Live - Better price",
  "Cancel Pre-Live - Change of mind or Not interested",
  "Cancel Pre-Live - No reason disclosed",
  "Cancel Pre Live - Incorrect Product Sold",
  "Delete Lead",
];

const CSV_HEADERS = [
  { label: "Created On", key: "createdAt" },
  { label: "Created Month", key: "createdMonth" },
  { label: "Created Year", key: "createdYear" },
  { label: "Updated On", key: "updatedAt" },
  { label: "Signed On", key: "contractSignedAt" },
  { label: "Signed Month", key: "contractSignedMonth" },
  { label: "Signed Year", key: "contractSignedYear" },
  { label: "Live Date", key: "liveDate" },
  { label: "Docusign Id", key: "envelopId" },
  { label: "Account No", key: "accountNo" },
  { label: "Agent", key: "agentName" },
  { label: "Customer", key: "customerName" },
  { label: "Email", key: "email" },
  { label: "Billing Email", key: "billingEmail" },
  { label: "Contact", key: "contact" },
  { label: "Business Name", key: "businessName" },
  { label: "Business Type", key: "businessType" },
  { label: "Designation", key: "designation" },
  { label: "Address", key: "address" },
  { label: "E-Billing Address", key: "eBillingAddress" },
  { label: "Current Provider", key: "currentProvider" },
  { label: "Contract Length", key: "contractLength" },

  { label: "Product Type", key: "productType" },
  { label: "Sale Type", key: "tarrifSaleType" },
  { label: "Tariff Name", key: "tarrifName" },
  { label: "Tariff Price", key: "tarrifPrice" },
  { label: "CLI Mobile No.", key: "cliMobileNo" },

  { label: "Download Min Speed", key: "dominSpeed" },
  { label: "Download Max Speed", key: "domaxSpeed" },
  { label: "Upload Min Speed", key: "upminSpeed" },
  { label: "Upload Max Speed", key: "upmaxSpeed" },

  { label: "Router Upgrade", key: "routerUpgrade" },
  { label: "Router Cost Type", key: "routerCostType" },
  { label: "No. of Routers", key: "noOfRouters" },
  { label: "Router Charges", key: "routerCharges" },

  { label: "Corded Cost Type", key: "cordedCostType" },
  { label: "No. of Corded", key: "noOfCorded" },
  { label: "Corded Charges", key: "cordedCharges" },

  { label: "Cordless Cost Type", key: "cordlessCostType" },
  { label: "No. of Cordless", key: "noOfCordless" },
  { label: "Cordless Charges", key: "cordlessCharges" },

  { label: "Equipment Cost Type", key: "equipmentCostType" },
  { label: "Equipment", key: "equipment" },
  { label: "Equipment Charges", key: "equipmentCharges" },
  { label: "Debit Detail", key: "debitDetail" },
  { label: "E-Billing", key: "eBilling" },

  { label: "Sale Status", key: "saleStatus" },
  { label: "Contract Status", key: "status" },
  { label: "Payment Collected", key: "isPaymentCollected" },
  // { label: "Notes", key: "allNotes" },
  { label: "Total Revenue", key: "revenue" },
  { label: "Total One-off Cost", key: "oneOfCost" },
  { label: "Total GP", key: "grossProfit" },

  // Direct Debit Details
  { label: "Account Holder Name", key: "bankAccountHolderName" },
  { label: "Account Number", key: "bankAccountNumber" },
  { label: "Sort Code", key: "bankSortCode" },
  { label: "Name and Address of Bank/Building Society", key: "bankAddress" },

  // Discount
  { label: "Discount", key: "discount" },
];

const AdminAllConnectionPage = () => {
  const csvRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [allContracts, setAllContracts] = useState([]);
  const [allProspects, setAllProspects] = useState([]);
  const [adminDetails, setAdminDetail] = useState(null);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debounceSearchTerm = useDebounce(
    searchKeys?.leadSearchValue,
    1000,
    true,
  );

  const { setSendType, userData, handleResetData } = useContext(AppContext);
  const paginatedData = useSelector((state) => state.contractPaginatedReducer);

  useEffect(() => {
    if (userData) fetchAdminDetail(userData?._id);
  }, [userData]);

  useEffect(() => {
    fetchAllContracts(
      debounceSearchTerm,
      searchKeys?.leadSaleStatusValue,
      searchKeys?.leadStartDate,
      searchKeys?.leadEndDate,
      paginatedData.page,
      paginatedData.limit,
      searchKeys?.leadSelectedAgents,
    );
  }, [
    debounceSearchTerm,
    searchKeys?.leadSaleStatusValue,
    searchKeys?.leadSelectedAgents,
    paginatedData.page,
    paginatedData.limit,
  ]);

  const fetchAllContracts = async (
    dt,
    ss,
    st,
    ed,
    pageToLoad,
    limit,
    agents = searchKeys?.leadSelectedAgents || [],
  ) => {
    try {
      setIsLoading(true);
      const response = await getAllContract(
        dt,
        "All",
        ss,
        st,
        ed,
        pageToLoad,
        limit,
        agents,
      );
      if (response?.data.status === "success") {
        let data = response?.data.data;
        let pagination = response?.data.pagination;
        setAllContracts(data);
        dispatch(setPageLimit(pagination.limit));
        dispatch(setTotalPages(pagination.totalPages));
        dispatch(setTotalCount(pagination.total));
        dispatch(setCurrentPage(pagination.page));
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdminDetail = async (id) => {
    const fields = "canExport,status";
    try {
      const response = await getAdminAgentDetails(id, fields);
      if (response.data.status === "success") {
        const data = response.data.data;
        setAdminDetail(data);
      }
    } catch (error) {}
  };

  const exportAllProspects = async () => {
    try {
      setIsExportLoading(true);
      const result = await exportContract();
      if (result.data.status === "success") {
        setAllProspects(result.data.data);
        setTimeout(() => {
          if (csvRef.current) {
            csvRef.current.link.click();
          }
        }, 100);
      } else {
        handleOpenSnackBar("error", result.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message || "Export failed");
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleUpdateSaleStatus = async (e, row) => {
    // Show SweetAlert confirmation prompt

    const payload = {
      saleStatus: e,
      log: {
        section: "Sale status update",
        field: "saleStatus",
        oldValue: row.saleStatus || "Empty",
        newValue: e || "Empty",
        message: `Sale status changed from ${row.saleStatus || "Empty"} to ${
          e || "Empty"
        }`,
        timestamp: Date.now(),
        changedBy: userData?.username,
      },
    };

    if (CANCEL_SALE_STATUS.includes(e)) {
      Swal.fire({
        // title: "Are you sure?",
        text: "CONTRACT END DATE MUST BE ADDED TO SALES LEAD",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Edit!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true);
            const response = await updateContractStatuses(row._id, payload);
            if (response?.data.status === "success") {
              fetchAllContracts(
                debounceSearchTerm,
                searchKeys?.leadSaleStatusValue,
                searchKeys?.leadStartDate,
                searchKeys?.leadEndDate,
                paginatedData.page,
                paginatedData.limit,
              );
              // handleOpenSnackBar("success", "Sale status updated successfully");
              Swal.fire({
                title: "Updated!",
                text: "The sale status has been updated.",
                icon: "success",
              });
            } else {
              handleOpenSnackBar("error", response?.data.message);
            }
          } catch (error) {
            handleOpenSnackBar("error", error?.message);
          } finally {
            setIsLoading(false);
          }
        }
      });
    } else {
      Swal.fire({
        title: "Are you sure?",
        text: "You want to update the status.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, update it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true);
            const response = await updateContractStatuses(row._id, payload);
            if (response?.data.status === "success") {
              fetchAllContracts(
                debounceSearchTerm,
                searchKeys?.leadSaleStatusValue,
                searchKeys?.leadStartDate,
                searchKeys?.leadEndDate,
                paginatedData.page,
                paginatedData.limit,
              );
              // handleOpenSnackBar("success", "Sale status updated successfully");
              Swal.fire({
                title: "Updated!",
                text: "The sale status has been updated.",
                icon: "success",
              });
            } else {
              handleOpenSnackBar("error", response?.data.message);
            }
          } catch (error) {
            handleOpenSnackBar("error", error?.message);
          } finally {
            setIsLoading(false);
          }
        }
      });
    }
  };

  const handleUpgradeContract = async (row) => {
    const willUpgrade = !row.upgradeContract;

    const swalConfig = {
      title: "Are you sure?",
      text: willUpgrade
        ? "Enter previous gross profit to upgrade this contract."
        : "You want to update the contract.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
      ...(willUpgrade && {
        input: "number",
        inputLabel: "Previous Gross Profit",
        inputPlaceholder: "Enter previous gross profit",
        inputAttributes: {
          min: "0",
          step: "any",
        },
        inputValidator: (value) => {
          if (value === "" || value === null || value === undefined) {
            return "Previous gross profit is required";
          }
          if (Number.isNaN(Number(value))) {
            return "Please enter a valid number";
          }
          return null;
        },
      }),
    };

    Swal.fire(swalConfig).then(async (result) => {
      if (result.isConfirmed) {
        const payload = {
          upgradeContract: willUpgrade,
          ...(willUpgrade && {
            previousGrossProfit: Number(result.value),
          }),
          logs: [
            {
              section: "Upgrade contract",
              field: "upgradeContract",
              oldValue: row.upgradeContract || false,
              newValue: willUpgrade,
              message: willUpgrade
                ? `Contract upgraded status changed from Not Upgraded to Upgraded. Previous gross profit: ${result.value}`
                : `Contract upgraded status changed from Upgraded to Not Upgraded`,
              timestamp: Date.now(),
              changedBy: userData?.username,
            },
          ],
        };

        try {
          setIsLoading(true);
          const response = await updateContract(row._id, payload);
          if (response?.data.status === "success") {
            fetchAllContracts(
              debounceSearchTerm,
              searchKeys?.leadSaleStatusValue,
              searchKeys?.leadStartDate,
              searchKeys?.leadEndDate,
              paginatedData.page,
              paginatedData.limit,
            );
            Swal.fire({
              title: "Updated!",
              text: "The contract upgraded status has been updated.",
              icon: "success",
            });
          } else {
            handleOpenSnackBar("error", response?.data.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleAgentViewStatus = (row, value) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to update the prospect status.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          const response = await updateContractStatuses(row._id, {
            canAgentSee: value,
          });
          if (response?.data.status === "success") {
            fetchAllContracts(
              debounceSearchTerm,
              searchKeys?.leadSaleStatusValue,
              searchKeys?.leadStartDate,
              searchKeys?.leadEndDate,
              paginatedData.page,
              paginatedData.limit,
            );

            Swal.fire({
              title: "Updated!",
              text: "The prospect status has been updated.",
              icon: "success",
            });
          } else {
            handleOpenSnackBar("error", response?.data.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleEditContract = (row) => {
    navigate("/edit-lead", { state: { data: row } });
    setSendType({
      type: "edit",
      id: row._id,
    });
  };

  const handleFilterByDate = () => {
    if (searchKeys?.leadStartDate && searchKeys?.leadEndDate) {
      dispatch(setCurrentPage(1));
      fetchAllContracts(
        debounceSearchTerm,
        searchKeys?.leadSaleStatusValue,
        searchKeys?.leadStartDate,
        searchKeys?.leadEndDate,
        1,
        paginatedData.limit,
        searchKeys?.leadSelectedAgents,
      );
    } else {
      handleOpenSnackBar("error", "Select date first");
    }
  };

  const handleReset = () => {
    dispatch(resetLeadSearchFilter());
    dispatch(resetPagination());
    // fetchAllContracts("", [], "", "");
  };

  const handleDeleteContract = (row) => {
    if (row.saleStatus === "Test") {
      deleteAlert(true, row?._id, deleteContract, () =>
        fetchAllContracts(
          debounceSearchTerm,
          searchKeys?.leadSaleStatusValue,
          searchKeys?.leadStartDate,
          searchKeys?.leadEndDate,
          paginatedData.page,
          paginatedData.limit,
        ),
      );
    } else {
      handleOpenSnackBar(
        "error",
        "The sale status should be in test mode when deleting a contract.",
      );
    }
  };

  const handleUpdateDocusignStatus = (e, row) => {
    const KEYS = {
      "envelope-sent": "Contract Sent",
      "envelope-delivered": "Contract Delievered",
      "envelope-completed": "Contract Completed",
      "envelope-resend": "Contract Resend",
    };

    const payload = {
      status: e,
      log: {
        section: "Sale status update",
        field: "saleStatus",
        oldValue: KEYS[row.status] || "Empty",
        newValue: KEYS[e] || "Empty",
        message: `Docusign status changed from ${
          KEYS[row.status] || "Empty"
        } to ${KEYS[e] || "Empty"}`,
        timestamp: Date.now(),
        changedBy: userData?.designation,
      },
    };

    Swal.fire({
      title: "Are you sure?",
      text: "You want to update the docusign status.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          const response = await updateContractStatuses(row._id, payload);
          if (response?.data.status === "success") {
            fetchAllContracts(
              debounceSearchTerm,
              searchKeys?.leadSaleStatusValue,
              searchKeys?.leadStartDate,
              searchKeys?.leadEndDate,
              paginatedData.page,
              paginatedData.limit,
            );
            Swal.fire({
              title: "Updated!",
              text: "The docusign status has been updated.",
              icon: "success",
            });
          } else {
            handleOpenSnackBar("error", response?.data.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  function processCliData(data) {
    return data.map((item) => {
      let selectedValues = new Set();

      if (item.legacyTariff.isSelected) {
        item.lineValues.forEach((line) => {
          if (line.legacy) {
            selectedValues.add(line.legacy.value);
          }
        });
      } else if (item.digitalTariff.isSelected) {
        item.lineValues.forEach((line) => {
          if (line.ngnValue) {
            selectedValues.add(line.ngnValue.value);
          }
        });
      }

      return Array.from(selectedValues);
    });
  }

  function processCliDataSaleType(data) {
    return data.map((item) => {
      let value = [];

      if (item?.upgradeCharge?.isSelected) {
        value.push("Install");
      } else if (item?.migration?.isSelected) {
        value.push("Migration");
      } else if (item?.transfer?.isSelected) {
        value.push("Transfer");
      }

      return value.join(", ");
    });
  }

  function processCliPrice(data) {
    return data.map((item) => {
      let selectedValues = new Set();

      if (item.legacyTariff.isSelected) {
        item.lineValues.forEach((line) => {
          if (line.legacy) {
            selectedValues.add(line.legacy.salePrice);
          }
        });
      } else if (item.digitalTariff.isSelected) {
        item.lineValues.forEach((line) => {
          if (line.ngnValue) {
            selectedValues.add(line.ngnValue.salePrice);
          }
        });
      }

      return Array.from(selectedValues);
    });
  }

  const downloadData = () => {
    return allProspects.map((val) => {
      const generalInfo = val.generalInfo || {};
      const eBillingAddress = val.eBillingAddress || {};
      const contractLength = val.contractLength || {};
      const downloadSpeed = val.downloadSpeed || {};
      const uploadSpeed = val.uploadSpeed || {};

      const routerValue = val.routerValue || {};
      const isRouterValue = val.routerValue.value > 0;

      const cordedPhone = val.cordedPhone || {};
      const isCordedPhone = val.cordedPhone.value > 0;

      const cordedlessPhone = val.cordedlessPhone || {};
      const isCordedlessPhone = val.cordedlessPhone.value > 0;

      const equipments = val.equipments || {};
      const isEquipments = val.equipments.value && val.equipments.price > 0;

      const CREATED_MONTH = `${moment(val.createdAt).format("MMMM")} `;
      const CREATED_YEAR = `${moment(val.createdAt).format("YYYY")} `;

      const SIGNED_DATE = moment(val.contractSignedAt).format(
        "DD.MM.YY [at] HH:mm",
      );

      const LIVE_DATE = moment(val?.generalInfo?.liveDate).format(
        "DD.MM.YY [at] HH:mm",
      );
      const SIGNED_YEAR = `${moment(val.contractSignedAt).format("YYYY")} `;
      const SIGNED_MONTH = `${moment(val.contractSignedAt).format("MMMM")} `;

      return {
        createdAt: moment(val.createdAt).format("DD.MM.YY [at] HH:mm") || "",
        createdMonth: val.createdAt ? CREATED_MONTH : "",
        createdYear: val.createdAt ? CREATED_YEAR : "",
        updatedAt: moment(val.updatedAt).format("DD.MM.YY [at] HH:mm") || "",
        contractSignedAt: val.contractSignedAt ? SIGNED_DATE : "",
        contractSignedMonth: val.contractSignedAt ? SIGNED_MONTH : "",
        contractSignedYear: val.contractSignedAt ? SIGNED_YEAR : "",
        liveDate: val?.generalInfo?.liveDate ? LIVE_DATE : "",
        envelopId: val.envelopId ? `'${val.envelopId}` : "",
        accountNo: val.accountNo ? `'${val.accountNo}` : "",
        agentName: generalInfo.agent || "",
        customerName: `${generalInfo.title || ""} ${
          generalInfo.name || ""
        }`.trim(),
        email: generalInfo.email || "",
        billingEmail: generalInfo.billingEmail || "",
        contact: generalInfo.mobileNo ? `'${generalInfo.mobileNo}` : "",
        businessName: generalInfo.businessName || "",
        businessType: generalInfo.businessType || "",
        designation: generalInfo.designation || "",
        address: [
          generalInfo?.address1,
          generalInfo?.address2,
          generalInfo?.town,
          generalInfo?.city,
          generalInfo?.postCode,
        ]
          .filter((value) => value)
          .join(", "),
        eBillingAddress: [
          eBillingAddress?.address1,
          eBillingAddress?.address2,
          eBillingAddress?.town,
          eBillingAddress?.city,
          eBillingAddress?.postCode,
        ]
          .filter((value) => value)
          .join(", "),
        currentProvider: generalInfo.currentProvider,
        contractLength: contractLength.title,
        productType: val.cliData
          ? val.cliData
              .map((item) =>
                item.legacyTariff.isSelected
                  ? "Legacy"
                  : item.digitalTariff.isSelected
                    ? "Digital"
                    : "N/A",
              )
              .join(", ")
          : "NA",

        tarrifSaleType: processCliDataSaleType(val.cliData),

        tarrifName: processCliData(val.cliData),
        tarrifPrice: processCliPrice(val.cliData),
        cliMobileNo: val.cliData
          ? val.cliData
              .map((item) =>
                item.cliNumber ? `${item.mobileNo || "N/A"}` : "New Connection",
              )
              .join(", ")
          : "NA",

        dominSpeed: downloadSpeed.min,
        domaxSpeed: downloadSpeed.max,
        upminSpeed: uploadSpeed.min,
        upmaxSpeed: uploadSpeed.max,

        routerUpgrade: routerValue.isUpgrade ? "True" : "False",
        routerCostType: isRouterValue ? routerValue.costType : "",
        noOfRouters: isRouterValue ? routerValue.value : "",
        routerCharges: isRouterValue ? routerValue.price : "",

        cordedCostType: isCordedPhone ? cordedPhone.costType : "",
        noOfCorded: isCordedPhone ? cordedPhone.value : "",
        cordedCharges: isCordedPhone ? cordedPhone.price : "",

        cordlessCostType: isCordedlessPhone ? cordedlessPhone.costType : "",
        noOfCordless: isCordedlessPhone ? cordedlessPhone.value : "",
        cordlessCharges: isCordedlessPhone ? cordedlessPhone.price : "",

        equipmentCostType: isEquipments ? equipments.costType : "",
        equipment: isEquipments ? equipments?.name || equipments.value : "",
        equipmentCharges: isEquipments ? equipments.price : "",

        debitDetail: val?.isDebitDetails ? "True" : "False",
        eBilling: generalInfo?.eBilling,

        saleStatus: val.saleStatus,
        status: val.status,
        isPaymentCollected: val.isUpfrontPaymentCollected,

        revenue: val.totalRevenue,
        oneOfCost: val.totalOneOfCost,
        grossProfit: val.totalGrossProfit,

        bankAccountHolderName: val.isDebitDetails
          ? val?.debitDetails?.username
          : "NA",
        bankAccountNumber: val.isDebitDetails
          ? val?.debitDetails?.accountNumber
            ? `'${val.debitDetails.accountNumber}`
            : "NA"
          : "NA",
        bankSortCode: val.isDebitDetails
          ? val?.debitDetails?.shortCode
            ? `'${val.debitDetails.shortCode}`
            : "NA"
          : "NA",
        bankAddress: val.isDebitDetails ? val?.debitDetails?.address : "NA",
        discount: val.discountDetails ? val?.discountDetails?.code : "None",
      };
    });
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setPageLimit(limit));
    dispatch(setCurrentPage(1));
  };

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setCurrentPage(page));
  };

  const handleLinkedContract = (row) => {
    navigate("/edit-lead", { state: { data: row?.linkedContractDetails[0] } });
    setSendType({
      type: "edit",
      id: row?.linkedContractDetails[0]?._id,
    });
  };

  const tableHeader = [
    "Prospect Details",
    "Connect Details",
    "Sale Value",
    "Docusign Status",
    "Sale Status",
    "Actions",
  ];

  const displayRows = [
    "customer_details",
    "connectDetails",
    "paymentDetails",
    "docusign_status",
    "sale_status",
    "actions",
  ];

  const renderTable = useMemo(() => {
    return (
      <DynamicTable
        tableData={allContracts}
        displayRows={displayRows}
        tableHeader={tableHeader}
        handleUpdateSaleStatus={handleUpdateSaleStatus}
        showEdit={true}
        showDelete={true}
        handleDelete={handleDeleteContract}
        handleEdit={handleEditContract}
        handleAgentViewStatus={handleAgentViewStatus}
        handleUpdateDocusignStatus={handleUpdateDocusignStatus}
        isPaginated={true}
        paginatedData={paginatedData}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
        handleLinkedContract={handleLinkedContract}
        handleUpgradeContract={handleUpgradeContract}
        showUpgradeContract={userData?.designation === "Admin" ? true : false}
      />
    );
  }, [allContracts]);

  return (
    <AppHeader>
      <AppLoader isVisible={isLoading} />

      <CSVLink
        data={downloadData()}
        headers={CSV_HEADERS}
        filename={"prospects.csv"}
        className="btn btn-primary"
        target="_blank"
        ref={csvRef}
      />

      <Stack
        gap={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        {adminDetails &&
          adminDetails?.status === true &&
          adminDetails?.canExport && (
            <GlobalButton
              variant={"custom_gradient"}
              title={
                isExportLoading ? "Exporting Prospects" : "Export Prospects"
              }
              disabled={isExportLoading}
              handleClickBtn={exportAllProspects}
              style={{
                width: "auto",
                height: 35,
                borderRadius: "8px",
              }}
            />
          )}
        <GlobalButton
          variant={"custom_gradient"}
          title={"Add Lead"}
          handleClickBtn={() => {
            navigate("/add-lead-admin");
            handleResetData(true);
            dispatch(setPhoneValidation(null));
            dispatch(setSignedDate(null));
          }}
          style={{
            width: "auto",
            height: 35,
            borderRadius: "8px",
          }}
        />
      </Stack>

      <SearchTab
        allContracts={allContracts}
        onDateFilter={handleFilterByDate}
        handleReset={handleReset}
      />
      {renderTable}
      {/* <DynamicTable
        tableData={allContracts}
        displayRows={displayRows}
        tableHeader={tableHeader}
        handleUpdateSaleStatus={handleUpdateSaleStatus}
        showEdit={true}
        showDelete={true}
        handleDelete={handleDeleteContract}
        handleEdit={handleEditContract}
        handleAgentViewStatus={handleAgentViewStatus}
        handleUpdateDocusignStatus={handleUpdateDocusignStatus}
        isPaginated={true}
        paginatedData={paginatedData}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
      /> */}
    </AppHeader>
  );
};

export default AdminAllConnectionPage;
